import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useI18n } from '../lib/i18n/I18nProvider';
import { useSession } from '../lib/auth/session';
import { BrandLogo } from '../ui/BrandLogo';

export interface NavSection {
  title: string;
  links: { to: string; label: string; end?: boolean }[];
}

type Theme = 'light' | 'dark';
type IconName =
  | 'approvals'
  | 'building'
  | 'customers'
  | 'dashboard'
  | 'developer'
  | 'expos'
  | 'floor'
  | 'identity'
  | 'language'
  | 'leads'
  | 'menu'
  | 'moon'
  | 'privacy'
  | 'sign-out'
  | 'sun';

const THEME_STORAGE_KEY = 'moqeem.theme';

function readInitialTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* A blocked storage area still gets a usable system-derived theme. */
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function iconForRoute(to: string): IconName {
  if (to.includes('approvals')) return 'approvals';
  if (to.includes('organizations') || to.includes('company')) return 'building';
  if (to.includes('customers')) return 'customers';
  if (to.includes('direct-leads') || to.includes('attribution')) return 'leads';
  if (to.includes('identities')) return 'identity';
  if (to.includes('layouts') || to.includes('floor')) return 'floor';
  if (to.includes('privacy') || to.includes('consents')) return 'privacy';
  if (to === '/admin') return 'expos';
  if (to === '/developer') return 'developer';
  return 'dashboard';
}

function Icon({ name }: { name: IconName }): ReactElement {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 1.8,
  };

  const paths: Record<IconName, ReactNode> = {
    approvals: <><path d="M7 3.8h10M8.5 2.5v2.6M15.5 2.5v2.6" /><rect x="4" y="4.5" width="16" height="16" rx="3" /><path d="m8 13 2.3 2.3L16 9.7" /></>,
    building: <><path d="M4 21h16M6 21V5l6-2 6 2v16" /><path d="M9 8h.01M15 8h.01M9 12h.01M15 12h.01M9 16h.01M15 16h.01" /></>,
    customers: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
    dashboard: <><rect x="3" y="3" width="7" height="7" rx="2" /><rect x="14" y="3" width="7" height="7" rx="2" /><rect x="3" y="14" width="7" height="7" rx="2" /><rect x="14" y="14" width="7" height="7" rx="2" /></>,
    developer: <><path d="M4 21V8l8-5 8 5v13" /><path d="M9 21v-6h6v6M8 10h.01M12 10h.01M16 10h.01" /></>,
    expos: <><path d="M3 7h18M5 7V4h14v3M5 7v13h14V7" /><path d="M9 11h6M9 15h6" /></>,
    floor: <><path d="m3 6 6-3 6 3 6-3v15l-6 3-6-3-6 3Z" /><path d="M9 3v15M15 6v15" /></>,
    identity: <><rect x="3" y="5" width="18" height="14" rx="3" /><circle cx="9" cy="11" r="2.2" /><path d="M6.5 16a3 3 0 0 1 5 0M14 10h4M14 14h3" /></>,
    language: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" /></>,
    leads: <><path d="M4 19v-1a4 4 0 0 1 4-4h3" /><circle cx="9" cy="7" r="4" /><path d="m15 16 2 2 4-5" /></>,
    menu: <><path d="M4 7h16M4 12h16M4 17h16" /></>,
    moon: <path d="M21 12.8A8.5 8.5 0 1 1 11.2 3 6.7 6.7 0 0 0 21 12.8Z" />,
    privacy: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" /><path d="m9 12 2 2 4-4" /></>,
    'sign-out': <><path d="M10 17l5-5-5-5M15 12H3" /><path d="M14 3h4a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3h-4" /></>,
    sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.65 17.65l1.42 1.42M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.65 6.35l1.42-1.42" /></>,
  };

  return <svg aria-hidden="true" className="icon" viewBox="0 0 24 24" {...common}>{paths[name]}</svg>;
}

/** The shared shell keeps scope visible while making the current task the visual priority. */
export function Shell({
  portalName,
  sections,
  environment,
}: {
  portalName: string;
  sections: NavSection[];
  environment: string | null;
}): ReactElement {
  const { t, locale, toggleLocale, dir, formatDateTime } = useI18n();
  const session = useSession();
  const location = useLocation();
  const grants = session.grants;
  const surfaceLabel = session.surface === 'dashboard' ? t('nav.surface.dashboard') : t('nav.surface.portal');
  const assuranceLabel = grants.length === 0 ? t('nav.assurance.standard') : t('nav.assurance.elevated');
  const [navOpen, setNavOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(readInitialTheme);

  const activeLabel = useMemo(() => {
    const links = sections.flatMap((section) => section.links);
    const exact = links.find((link) => link.to === location.pathname);
    if (exact) return exact.label;
    const parent = [...links]
      .sort((a, b) => b.to.length - a.to.length)
      .find((link) => location.pathname.startsWith(`${link.to}/`));
    return parent?.label ?? portalName;
  }, [location.pathname, portalName, sections]);

  useEffect(() => setNavOpen(false), [location.pathname]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* Theme remains active for this session even if persistence is blocked. */
    }
  }, [theme]);

  return (
    <div className="shell" dir={dir}>
      <a className="visually-hidden" href="#main">{t('nav.skip')}</a>
      {environment ? <div className="shell__banner" role="status">{t('nav.environment', { env: environment })}</div> : null}

      <nav className={`shell__nav${navOpen ? ' shell__nav--open' : ''}`} id="primary-navigation" aria-label={portalName}>
        <div className="shell__brand">
          <BrandLogo className="shell__logo" surface="dark" alt={t('brand.name')} />
          <span className="shell__brand-copy"><strong>{portalName}</strong></span>
          <button className="shell__nav-close" type="button" onClick={() => setNavOpen(false)} aria-label={t('nav.close')}>
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <div className="shell__scope-card">
          <span className="shell__scope-label">{t('nav.currentScope')}</span>
          <dl className="shell__scope">
            <div><dt>{t('nav.scope.surface')}</dt><dd>{surfaceLabel}</dd></div>
            <div><dt>{t('nav.scope.assurance')}</dt><dd data-testid="scope-assurance">{assuranceLabel}</dd></div>
          </dl>
        </div>

        {grants.length > 0 ? (
          <ul className="shell__grants" data-testid="step-up-grants">
            {grants.map((g) => <li key={g.action} className="badge badge--warn"><span className="ltr">{g.action}</span> · {formatDateTime(g.expiresAt)}</li>)}
          </ul>
        ) : null}

        <div className="shell__navigation">
          {sections.map((section, sectionIndex) => (
            <section className="shell__section" key={section.title} aria-labelledby={`nav-section-${sectionIndex}`}>
              <h2 id={`nav-section-${sectionIndex}`}>{section.title}</h2>
              {section.links.map((link) => (
                <NavLink className="shell__link" key={link.to} to={link.to} end={link.end ?? false}>
                  <Icon name={iconForRoute(link.to)} /><span>{link.label}</span>
                </NavLink>
              ))}
            </section>
          ))}
        </div>

        <div className="shell__footer">
          <button type="button" className="shell__utility" onClick={toggleLocale}><Icon name="language" /><span>{t('lang.toggle')}</span></button>
          <button type="button" className="shell__utility shell__utility--danger" onClick={() => void session.signOut()}><Icon name="sign-out" /><span>{t('nav.signOut')}</span></button>
          <span className="shell__tagline" lang={locale}>{t('brand.tagline')}</span>
        </div>
      </nav>

      {navOpen ? <button type="button" className="shell__backdrop" onClick={() => setNavOpen(false)} aria-label={t('nav.close')} /> : null}

      <div className="shell__workspace">
        <header className="shell__topbar">
          <div className="shell__topbar-title">
            <button type="button" className="shell__menu" onClick={() => setNavOpen(true)} aria-label={t('nav.open')} aria-controls="primary-navigation" aria-expanded={navOpen}>
              <Icon name="menu" />
            </button>
            <span><span className="shell__eyebrow">{portalName}</span><strong>{activeLabel}</strong></span>
          </div>
          <div className="shell__topbar-actions">
            <button type="button" className="shell__topbar-action" onClick={toggleLocale} aria-label={t('lang.label')}><Icon name="language" /><span>{t('lang.toggle')}</span></button>
            <button type="button" className="shell__topbar-action" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} aria-label={`${t('theme.toggle')}: ${theme === 'dark' ? t('theme.light') : t('theme.dark')}`}>
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} /><span>{theme === 'dark' ? t('theme.light') : t('theme.dark')}</span>
            </button>
          </div>
        </header>
        <main className="shell__main" id="main" tabIndex={-1}><Outlet /></main>
      </div>
    </div>
  );
}

export function ShellFrame({ children }: { children: ReactNode }): ReactElement {
  return <>{children}</>;
}
